import React, { useEffect, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiExternalLink,
  FiMonitor,
  FiRadio,
  FiSend,
  FiTablet,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import './LiveStream.css';

const STORAGE_URL = 'liveStream.mtsUrl';
const STORAGE_VIEW = 'liveStream.view';
const ALLOWED_HOSTS = ['mts-link.ru', 'webinar.ru'];

const STARTER_MESSAGES = [
  {
    id: 'sys-1',
    author: 'Система',
    role: 'system',
    text: 'Это наш чат, не чат МТС Линк. Пока сообщения живут только в этой вкладке.',
  },
];

function isAllowedLiveUrl(value) {
  try {
    const parsed = new URL((value || '').trim());
    if (parsed.protocol !== 'https:') return false;
    const host = parsed.hostname.toLowerCase();
    return ALLOWED_HOSTS.some((domain) => host === domain || host.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

function formatClock(date) {
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

const LiveStream = () => {
  const [urlInput, setUrlInput] = useState(() => localStorage.getItem(STORAGE_URL) || '');
  const [loadedUrl, setLoadedUrl] = useState('');
  const [view, setView] = useState(() => localStorage.getItem(STORAGE_VIEW) || 'studio');
  const [chatText, setChatText] = useState('');
  const [messages, setMessages] = useState(STARTER_MESSAGES);
  const [showEmbedHint, setShowEmbedHint] = useState(false);
  const chatEndRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const applyUrl = (event) => {
    event?.preventDefault();
    const nextUrl = urlInput.trim();
    if (!isAllowedLiveUrl(nextUrl)) {
      toast.error('Нужна https-ссылка на mts-link.ru или webinar.ru');
      return;
    }
    localStorage.setItem(STORAGE_URL, nextUrl);
    setLoadedUrl(nextUrl);
    setShowEmbedHint(true);
  };

  const openExternally = () => {
    const target = loadedUrl || urlInput.trim();
    if (!isAllowedLiveUrl(target)) {
      toast.error('Сначала вставьте корректную ссылку на эфир');
      return;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const sendMessage = (event) => {
    event?.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        author: 'Вы',
        role: 'self',
        text,
        time: formatClock(new Date()),
      },
    ]);
    setChatText('');
  };

  const changeView = (nextView) => {
    setView(nextView);
    localStorage.setItem(STORAGE_VIEW, nextView);
  };

  return (
    <div className={`live-page live-page--${view}`}>
      <form className="live-toolbar" onSubmit={applyUrl}>
        <div className="live-toolbar-brand">
          <span className={`live-dot ${loadedUrl ? 'is-on' : ''}`} />
          <div>
            <strong>Прямой эфир</strong>
            <span>Тест iframe МТС Линк</span>
          </div>
        </div>

        <label className="live-url-field">
          <span>Ссылка на комнату</span>
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://my.mts-link.ru/..."
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <div className="live-toolbar-actions">
          <button type="submit" className="live-btn live-btn-primary">
            <FiRadio /> Загрузить в iframe
          </button>
          <button type="button" className="live-btn" onClick={openExternally}>
            <FiExternalLink /> Новая вкладка
          </button>
          <div className="live-view-toggle" role="group" aria-label="Режим экрана">
            <button
              type="button"
              className={view === 'studio' ? 'is-active' : ''}
              onClick={() => changeView('studio')}
              title="Эфир и чат"
            >
              <FiMonitor /> Студия
            </button>
            <button
              type="button"
              className={view === 'chat' ? 'is-active' : ''}
              onClick={() => changeView('chat')}
              title="Только чат, для планшета"
            >
              <FiTablet /> Чат
            </button>
          </div>
        </div>
      </form>

      {showEmbedHint && view === 'studio' && (
        <div className="live-hint">
          <FiAlertTriangle />
          <p>
            Если внутри пусто или ошибка — МТС Линк часто запрещает встраивание
            через <code>X-Frame-Options</code>. Тогда эфир откройте в новой вкладке,
            а чат оставьте здесь.
          </p>
          <button type="button" className="live-hint-close" onClick={() => setShowEmbedHint(false)}>
            <FiX />
          </button>
        </div>
      )}

      <div className="live-stage">
        {view === 'studio' && (
          <section className="live-frame-card">
            {loadedUrl ? (
              <iframe
                ref={iframeRef}
                title="Эфир МТС Линк"
                src={loadedUrl}
                allow="camera; microphone; autoplay; fullscreen; display-capture; clipboard-read; clipboard-write"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="live-empty">
                <FiRadio />
                <h2>Комната пока не загружена</h2>
                <p>
                  Вставьте ссылку на вебинар МТС Линк и нажмите «Загрузить в iframe».
                  Рядом уже работает наш чат — его потом подключим к эфиру.
                </p>
              </div>
            )}
          </section>
        )}

        <aside className="live-chat-card">
          <header className="live-chat-header">
            <div>
              <strong>Чат эфира</strong>
              <span>наш прототип</span>
            </div>
            <span className="live-chat-count">{messages.length}</span>
          </header>

          <div className="live-chat-list">
            {messages.map((message) => (
              <article
                key={message.id}
                className={`live-chat-item live-chat-item--${message.role || 'user'}`}
              >
                <div className="live-chat-meta">
                  <b>{message.author}</b>
                  {message.time ? <time>{message.time}</time> : null}
                </div>
                <p>{message.text}</p>
              </article>
            ))}
            <div ref={chatEndRef} />
          </div>

          <form className="live-chat-compose" onSubmit={sendMessage}>
            <input
              type="text"
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              placeholder="Написать в наш чат…"
              maxLength={500}
            />
            <button type="submit" disabled={!chatText.trim()} aria-label="Отправить">
              <FiSend />
            </button>
          </form>
        </aside>
      </div>
    </div>
  );
};

export default LiveStream;
